import {getGeolocationPlatform, isStandalonePwa} from "@/src/modules/core/lib/geolocation-platform";

import {
  getCameraEnvironmentDiagnostics,
  releaseMediaStream,
  requestCameraVideoStream,
  type CameraEnvironmentDiagnostics,
} from "./camera-environment";

export type CameraPermissionState = "granted" | "denied" | "prompt" | "unknown";

export {getCameraEnvironmentDiagnostics, releaseMediaStream, requestCameraVideoStream};
export type {CameraEnvironmentDiagnostics} from "./camera-environment";

export async function queryCameraPermissionState(): Promise<CameraPermissionState> {
  if (typeof navigator === "undefined" || !navigator.permissions?.query) {
    return "unknown";
  }

  try {
    const status = await navigator.permissions.query({
      name: "camera" as PermissionName,
    });
    return status.state as CameraPermissionState;
  } catch {
    return "unknown";
  }
}

function getCameraSettingsHint(): string {
  const platform = getGeolocationPlatform();
  const installed = isStandalonePwa();

  if (platform === "android") {
    if (installed) {
      return (
        "Camera for the installed app is managed in Chrome site settings, not under " +
        "Settings → Apps → StarsPro (that screen often shows “No permissions requested”). " +
        "Open Chrome → ⋮ → Settings → Site settings → Camera, find this site, and allow access."
      );
    }
    return "Open your browser site settings and allow Camera for this site.";
  }

  if (platform === "ios") {
    if (installed) {
      return "Open Settings → StarsPro → Camera and allow access.";
    }
    return "Open Settings → Safari → Camera (or this site’s settings) and allow access.";
  }

  return "Allow camera access in your browser’s site settings.";
}

export type CameraPermissionDialogContext = {
  permissionState: CameraPermissionState;
  mediaErrorName?: string;
};

/** GlitchTip `diagnosis_hint` — classifies likely root cause for on-call triage. */
export type CameraBlockDiagnosis =
  | "chrome_global_or_site_denied"
  | "chrome_pwa_prompt_suppressed"
  | "permissions_policy_blocked"
  | "no_get_user_media"
  | "insecure_context"
  | "no_user_activation"
  | "security_error"
  | "hardware_unavailable"
  | "not_allowed_unknown"
  | "unknown";

export function diagnoseCameraBlockReason(input: {
  permissionState: CameraPermissionState;
  mediaErrorName?: string;
  environment: CameraEnvironmentDiagnostics;
}): CameraBlockDiagnosis {
  const {permissionState, mediaErrorName, environment} = input;

  if (!environment.hasGetUserMedia) {
    return "no_get_user_media";
  }
  if (!environment.secureContext) {
    return "insecure_context";
  }
  if (!environment.permissionsPolicyAllowsCamera) {
    return "permissions_policy_blocked";
  }
  if (mediaErrorName === "SecurityError") {
    return "security_error";
  }
  if (
    mediaErrorName === "NotReadableError" ||
    mediaErrorName === "NotFoundError" ||
    mediaErrorName === "OverconstrainedError"
  ) {
    return "hardware_unavailable";
  }
  if (mediaErrorName === "NotAllowedError") {
    if (permissionState === "denied") {
      return "chrome_global_or_site_denied";
    }
    if (!environment.userActivationActive) {
      return "no_user_activation";
    }
    if (environment.standalonePwa && environment.platform === "android") {
      return "chrome_pwa_prompt_suppressed";
    }
    return "not_allowed_unknown";
  }
  return "unknown";
}

export function getCameraPermissionDialogDescription({
  permissionState,
  mediaErrorName,
}: CameraPermissionDialogContext): string {
  const errorSuffix = mediaErrorName ? ` (${mediaErrorName})` : "";
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? ` Site: ${window.location.origin}.`
      : "";

  if (permissionState === "granted") {
    return (
      "Your browser reports camera access is allowed, but the in-app camera still could not start" +
      errorSuffix +
      ". Close StarsPro completely and reopen it, then tap Try again. " +
      "If it still fails, use Gallery to add a photo." +
      origin
    );
  }

  if (permissionState === "prompt") {
    if (
      mediaErrorName === "NotAllowedError" &&
      getGeolocationPlatform() === "android" &&
      isStandalonePwa()
    ) {
      return (
        "The installed app did not show Android's camera prompt (a known Chrome PWA issue). " +
        "Open Chrome → ⋮ → Settings → Site settings → Camera, find this site, set Allow, " +
        "then fully close StarsPro and reopen. Or open this site once in a Chrome tab, allow camera there, then retry here." +
        origin
      );
    }

    return (
      "Tap Try again and allow camera access when prompted. This keeps you in the app while taking photos." +
      origin
    );
  }

  if (permissionState === "denied" && getGeolocationPlatform() === "android") {
    return (
      "Camera is blocked for this site in Chrome — clearing app permissions in Android Settings does not reset that. " +
      getCameraSettingsHint() +
      " Then fully close StarsPro and reopen, or use Gallery." +
      origin
    );
  }

  if (mediaErrorName === "SecurityError") {
    return (
      "Camera could not start in this app context" +
      errorSuffix +
      ". Close StarsPro completely, reopen it, and tap Try again, or use Gallery to add a photo."
    );
  }

  return `${getCameraSettingsHint()} Then return here and tap Try again, or use Gallery to add a photo instead.${origin}`;
}

export function getCameraUnavailableDialogDescription(details: {
  mediaErrorName?: string;
  reason?: string;
}): string {
  if (details.reason === "get_user_media_unsupported") {
    return "This device or browser cannot use the in-app camera. Use Gallery to add a photo.";
  }

  if (details.mediaErrorName === "NotReadableError") {
    return "The camera is in use by another app. Close other camera apps, then tap Try again, or use Gallery.";
  }

  if (
    details.mediaErrorName === "NotFoundError" ||
    details.mediaErrorName === "OverconstrainedError"
  ) {
    return "No compatible camera was found for the in-app preview. Use Gallery to add a photo, or tap Try again.";
  }

  if (details.mediaErrorName) {
    return `Couldn't open the in-app camera (${details.mediaErrorName}). Tap Try again, or use Gallery to add a photo.`;
  }

  return "Couldn't open the in-app camera. Tap Try again, or use Gallery to add a photo.";
}

/**
 * Android installed PWAs: gallery still backgrounds the WebView (kill risk on low memory).
 * In-app camera (getUserMedia) is the safe path — never re-enable native `<input capture>`.
 */
export function getGalleryPwaMemoryHint(): string | null {
  if (!isStandalonePwa() || getGeolocationPlatform() !== "android") {
    return null;
  }

  return (
    "Gallery may background the app and restart it on low-memory devices. " +
    "Your form draft is saved and should restore automatically. " +
    "Prefer Try again for the in-app camera when possible."
  );
}
